import axios from "../axios"
import type { FeedPost, FeedComment, FeedPortfolio } from "../types/feed.ts"

const getAuthHeaders = (): { headers?: Record<string, string> } => {
  const token = localStorage.getItem("access-Token-Edify")
  if (!token) return {}
  return { headers: { Authorization: `Bearer ${token}` } }
}

export const feedService = {
  getPosteos: async (order: string, type: string): Promise<FeedPost[]> => {
    const { data } = await axios.get(`posteo/list?order=${order}&type=${type}`, getAuthHeaders())
    return Array.isArray(data) ? data : []
  },

  getOwnPosteos: async (userId: number | string): Promise<FeedPost[]> => {
    const { data } = await axios.get(`posteo/user?userFind=${userId}`, getAuthHeaders())
    return Array.isArray(data) ? data : []
  },

  getAllPortfolios: async (): Promise<FeedPortfolio[]> => {
    const { data } = await axios.get("/portfolio/getAll", getAuthHeaders())
    return Array.isArray(data) ? data : []
  },

  getUserPortfolios: async (userId: number | string): Promise<FeedPortfolio[]> => {
    const { data } = await axios.get(
      `/portfolio/user?userFind=${userId}`,
      getAuthHeaders()
    )
    return Array.isArray(data) ? data : []
  },

  getPostComments: async (postId: number): Promise<FeedComment[]> => {
    const { data } = await axios.get(`/posteo/comments/${postId}`, getAuthHeaders())
    const response = data?.response
    return Array.isArray(response) ? response : []
  },

  getPostById: async (id: number): Promise<FeedPost> => {
    const { data } = await axios.get(`/posteo/${id}`, getAuthHeaders())
    return data
  },

  getPostWithComments: async (
    id: number
  ): Promise<{ post: FeedPost; comments: FeedComment[] }> => {
    const [postRes, commentsRes] = await Promise.all([
      axios.get(`/posteo/${id}`, getAuthHeaders()),
      axios.get(`/posteo/comments/${id}`, getAuthHeaders()),
    ])
    const comments = Array.isArray(commentsRes.data?.response)
      ? commentsRes.data.response
      : []
    return { post: postRes.data, comments }
  },

  likePost: async (postId: number | string): Promise<{ likes?: FeedPost["likes"] }> => {
    const id = Number(postId)
    const { data } = await axios.post<{ likes?: FeedPost["likes"] }>(
      "/posteo/like",
      { post_id: id },
      getAuthHeaders()
    )
    return data ?? {}
  },

  createPost: async (body: {
    content?: string
    video?: string
    images?: unknown
  }): Promise<FeedPost> => {
    const formData = new FormData()
    formData.append("content", body.content ?? "")
    formData.append("video", body.video ?? "")
    formData.append("images", (body.images as string) ?? "")
    const auth = getAuthHeaders()
    const { data } = await axios.post<FeedPost>("/posteo/create", formData, {
      headers: { ...auth.headers, "content-type": "multipart/form-data" },
    })
    return data
  },

  createPostComment: async (
    postId: number,
    content: string
  ): Promise<void> => {
    await axios.post(
      "/posteo/comment",
      { postId, content },
      getAuthHeaders()
    )
  },

  editPostComment: async (
    commentId: number,
    content: string
  ): Promise<void> => {
    await axios.patch(
      "/posteo/comment/update",
      { commentId, content },
      getAuthHeaders()
    )
  },

  deletePostComment: async (commentId: number): Promise<void> => {
    const auth = getAuthHeaders()
    await axios.delete("/posteo/comment/delete", {
      headers: auth.headers,
      data: { idComment: commentId },
    })
  },

  likePostComment: async (commentId: number): Promise<void> => {
    await axios.post(
      "/posteo/comment/like",
      { post_comment_id: commentId },
      getAuthHeaders()
    )
  },

  responsePostComment: async (
    postId: number,
    commentId: number,
    content: string
  ): Promise<void> => {
    await axios.post(
      "/posteo/comment/response",
      { postId, commentId, content },
      getAuthHeaders()
    )
  },

  deletePost: async (postId: number): Promise<void> => {
    const auth = getAuthHeaders()
    await axios.delete("/posteo/delete", {
      headers: auth.headers,
      data: { idPost: postId },
    })
  },

  updatePost: async (body: {
    id: number
    content?: string
    images?: unknown
    video?: unknown
  }): Promise<FeedPost> => {
    const formData = new FormData()
    formData.append("id", String(body.id))
    formData.append("content", body.content ?? "")
    formData.append("video", String(body.video ?? ""))
    formData.append("images", String(body.images ?? ""))
    const auth = getAuthHeaders()
    const { data } = await axios.patch("/posteo/update", formData, {
      headers: { ...auth.headers, "content-type": "multipart/form-data" },
    })
    return data
  },
}
